import React, { useState } from "react";
import { enqueueSnackbar } from "notistack";
import manualChange from "../../../controllers/booking/postTicketing/change/manualChange";
import addChangeMessage from "../../../controllers/booking/postTicketing/change/addMessage";
import Button1 from "../../form/Button1";
import StaffApiErrorModal, {
  getStaffApiErrorCopy,
} from "../StaffApiErrorModal";
import {
  changeOrderConfirmTravellers,
  changeOrderContructFlightDetails,
  changeOrderRouteChange,
} from "../../../controllers/ticketManagement/changeDateControllers";

export function buildManualChangePayload(data, extras = {}) {
  const payload = {
    changeOrderId: data?._id,
  };
  const selectedOffer =
    typeof data?.selectedOffer === "string"
      ? JSON.parse(data.selectedOffer)
      : data?.selectedOffer || null;
  const booking = Array.isArray(data?.flightBooking)
    ? data.flightBooking[0]
    : data?.flightBooking;

  if (data?.newSegment?.length || selectedOffer) {
    if (selectedOffer) {
      const routeChange = changeOrderRouteChange(
        booking || data?.flightBooking,
        selectedOffer,
      );
      if (!routeChange) {
        const { flights, destinations } = changeOrderContructFlightDetails(data);
        payload.flights = flights;
        payload.destinations = destinations;
      } else {
        const segments = selectedOffer?.directions?.flatMap(
          (direction, index) =>
            direction.map((segment) => ({
              ...segment,
              segment: `${index + 1}`,
            })),
        );
        payload.flights = segments.map((segment) => ({
          ...segment,
          aircraftCode: segment.aircraftCode,
          flightNumber: segment.flightNumber,
          departureTime: segment.departure.time,
          departureDate: segment.departure.date,
          departureLocation: segment.departure.location,
          departureAirport: segment.departure.airport,
          departureLocationName: segment.departure.city,
          arrivalTime: segment.arrival.time,
          arrivalDate: segment.arrival.date,
          arrivalLocation: segment.arrival.location,
          arrivalAirport: segment.arrival.airport,
          arrivalLocationName: segment.arrival.city,
          cabinClass: segment.cabinClass,
          bookingClass: segment.bookingClass,
          segmentRef: segment.segmentRef,
          airlineImage: segment.airline.image.url,
          airlineName: segment.airline.image.description,
          airline: segment.airline.marketing,
        }));
        payload.destinations = (selectedOffer?.destinations || []).map(
          (destination) => ({
            from: destination?.from || "",
            to: destination?.to || "",
            departureDate: destination?.departure?.date || "",
          }),
        );
      }
    } else {
      payload.flights = (booking?.flights || []).map((flight) => {
        const segment = data?.newSegment?.find(
          (seg) => seg?.segmentRef === flight?.segmentRef,
        );
        if (!segment) return flight;
        return {
          ...flight,
          _id: segment.id || flight?._id,
          stops: flight?.stops,
          aircraftCode: segment.aircraftCode || flight?.aircraftCode,
          flightNumber: segment.flightNumber || flight?.flightNumber,
          departureTime: segment.departure.time || flight?.departureTime,
          departureDate: segment.departure.date || flight?.departureDate,
          departureLocation:
            segment.departure.location || flight?.departureLocation,
          departureAirport:
            segment.departure.airport || flight?.departureAirport,
          departureLocationName:
            segment.departure.city || flight?.departureLocationName,
          arrivalTime: segment.arrival.time || flight?.arrivalTime,
          arrivalDate: segment.arrival.date || flight?.arrivalDate,
          arrivalLocation: segment.arrival.location || flight?.arrivalLocation,
          arrivalAirport: segment.arrival.airport || flight?.arrivalAirport,
          arrivalLocationName:
            segment.arrival.city || flight?.arrivalLocationName,
          cabinClass: segment.cabinClass || flight?.cabinClass,
          bookingClass: segment.bookingClass || flight?.bookingClass,
          segmentRef: segment.segmentRef || flight?.segmentRef,
          airlineImage: segment.airline.image.url || flight?.airlineImage,
          airlineName:
            segment.airline.image.description || flight?.airlineName,
          airline: segment.airline.marketing || flight?.airline,
        };
      });
      payload.destinations = (booking?.destinations || []).map((obj, i) => {
        const newDate = data?.newDirections?.find(
          (dir) => Number(dir?.segment) - 1 === i,
        )?.departure;
        return {
          ...obj,
          departureDate: newDate ? newDate : obj?.departureDate,
        };
      });
    }
  } else if (data?.changeRequest === "PassengerInfo") {
    payload.travelers = data?.passengerInfo;
  }
  if (data?.passengerInfo?.length) {
    payload.travelers = changeOrderConfirmTravellers(data);
  }

  // Task 7 — success recording fields (backend may store or ignore extras)
  if (extras.supplierConfirmation) {
    payload.supplierConfirmation = extras.supplierConfirmation;
  }
  if (extras.newTicketNumber) {
    payload.newTicketNumber = extras.newTicketNumber;
    payload.ticketNumber = extras.newTicketNumber;
  }
  if (extras.exchangeReference) {
    payload.exchangeReference = extras.exchangeReference;
    payload.reissueReference = extras.exchangeReference;
  }
  if (extras.notes) {
    payload.notes = extras.notes;
    payload.remark = extras.notes;
  }

  return payload;
}

const inputClass =
  "w-full h-9 px-2.5 text-sm border border-[#DBDBDB] rounded-lg bg-white text-[#0F172A] focus:outline-none focus:border-[#94A3B8]";

export default function ManualUpdate({ data, callback, close }) {
  const [loadings, setLoadings] = useState({ submit: false });
  const [apiError, setApiError] = useState(null);
  const [supplierConfirmation, setSupplierConfirmation] = useState(
    data?.supplierConfirmation || "",
  );
  const [newTicketNumber, setNewTicketNumber] = useState(
    data?.newTicketNumber || "",
  );
  const [exchangeReference, setExchangeReference] = useState(
    data?.exchangeReference || data?.reissueReference || "",
  );
  const [notes, setNotes] = useState(data?.remark || data?.notes || "");

  async function handleManual() {
    setLoadings({ ...loadings, submit: true });
    const extras = {
      supplierConfirmation: supplierConfirmation.trim(),
      newTicketNumber: newTicketNumber.trim(),
      exchangeReference: exchangeReference.trim(),
      notes: notes.trim(),
    };
    const res = await manualChange(buildManualChangePayload(data, extras));
    if (res.return && extras.notes) {
      try {
        await addChangeMessage(data?._id, {
          message: [
            "[SUCCESSFUL_EXCHANGE]",
            extras.supplierConfirmation
              ? `Supplier confirmation: ${extras.supplierConfirmation}`
              : null,
            extras.newTicketNumber
              ? `New ticket: ${extras.newTicketNumber}`
              : null,
            extras.exchangeReference
              ? `Exchange ref: ${extras.exchangeReference}`
              : null,
            extras.notes,
          ]
            .filter(Boolean)
            .join("\n"),
        });
      } catch (_) {
        /* remark is best-effort */
      }
    }
    setLoadings({ ...loadings, submit: false });
    if (res.return) {
      enqueueSnackbar("Exchange recorded. Original ticket kept for audit.", {
        variant: "success",
      });
      callback && callback();
    } else setApiError(getStaffApiErrorCopy(res));
  }

  return (
    <>
      <div className="flex flex-col gap-4 max-w-[520px]">
        <div>
          <h5 className="text-base font-semibold text-gray-900">
            Record successful exchange
          </h5>
          <p className="text-sm text-gray-600 mt-1">
            Only continue if the airline has already reissued a new ticket. This
            writes the new ticket into Intraverse. The original ticket is kept
            for audit. This does not take more money.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wide text-[#94A3B8] font-semibold">
              Supplier confirmation
            </label>
            <input
              className={`${inputClass} mt-1`}
              value={supplierConfirmation}
              onChange={(e) => setSupplierConfirmation(e.target.value)}
              placeholder="Airline confirmation / PNR note"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wide text-[#94A3B8] font-semibold">
              New ticket number
            </label>
            <input
              className={`${inputClass} mt-1`}
              value={newTicketNumber}
              onChange={(e) => setNewTicketNumber(e.target.value)}
              placeholder="New ticket #"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="text-[10px] uppercase tracking-wide text-[#94A3B8] font-semibold">
              Exchange / reissue reference
            </label>
            <input
              className={`${inputClass} mt-1`}
              value={exchangeReference}
              onChange={(e) => setExchangeReference(e.target.value)}
              placeholder="Exchange or reissue reference"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="text-[10px] uppercase tracking-wide text-[#94A3B8] font-semibold">
              Notes
            </label>
            <textarea
              className="mt-1 w-full min-h-[72px] px-2.5 py-2 text-sm border border-[#DBDBDB] rounded-lg"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Anything Ops should keep on this exchange…"
            />
          </div>
        </div>

        <div className="flex gap-3 justify-end">
          {close ? (
            <Button1 onClick={() => close()} variant="outlined">
              Go back
            </Button1>
          ) : null}
          <Button1 loading={loadings.submit} onClick={handleManual}>
            Mark successful / write ticket
          </Button1>
        </div>
      </div>
      <StaffApiErrorModal
        open={Boolean(apiError)}
        setOpen={(next) => {
          if (!next) setApiError(null);
        }}
        title={apiError?.title}
        text={apiError?.text}
      />
    </>
  );
}
